/**
 * Client-brand API types and TanStack Query hooks.
 *
 * Types mirror the Pydantic schemas in
 * `backend/servers/social/schemas/client_brands.py`.
 *
 * @module api/clientBrands
 */

import { useApiQuery } from "@plerity/dashboard-shared/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { client } from "./client";
import { socialKeys } from "./keys";
import { objectResponseGuard } from "./schemas";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ClientBrand {
  id: string;
  org_id: number;
  name: string;
  slug: string;
  crm_company_id: number | null;
  parent_brand_id: string | null;
  primary_color: string | null;
  logo_url: string | null;
  description: string | null;
  is_archived: boolean;
  /**
   * TRUE = an active social client: listed in every client picker, the
   * Clients overview and the attention list. FALSE = a live brand we don't run
   * social for (web-only client) — only returned with `include_inactive` /
   * `include_archived`. Migration 090. Optional so an older API build renders.
   */
  is_social_active?: boolean;
  /**
   * Meta Ads ad-account id (with `act_` prefix, e.g. `act_1148426616062609`).
   * NULL = brand has no linked Meta ad account → UI shows "Link a Meta ad
   * account" empty-state CTA. Migration 011 (2026-05-27).
   */
  meta_ad_account_id: string | null;
  /** Meta page id used for lead-form routing (matches webhooks.py config). */
  meta_page_id: string | null;
  /** Meta pixel id for conversion tracking + CAPI events. */
  meta_pixel_id: string | null;
  /**
   * TRUE when this brand is a HIPAA-covered entity (Animas has a BAA).
   * Drives PII masking, banner rendering, raw-payload gating, and
   * audit-log requirements on the Paid Media surface. See Phase 5
   * HIPAA posture in the plan.
   */
  is_hipaa_covered: boolean;
  /** IANA zone (e.g. `America/Denver`) — the composer's default zone for this client. Migration 054. */
  timezone?: string | null;
  /** Booking link — the `{{booking_url}}` snippet variable. Migration 054. */
  booking_url?: string | null;
  /** Public phone — the `{{phone}}` snippet variable. Migration 054. */
  phone?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Editable client profile (POST/PUT /api/client-brands). On update an empty
 * string clears a nullable field; an omitted key leaves it unchanged.
 */
export interface ClientBrandInput {
  name?: string;
  primary_color?: string;
  logo_url?: string;
  timezone?: string;
  booking_url?: string;
  phone?: string;
  is_archived?: boolean;
  is_social_active?: boolean;
}

export interface ClientBrandOverview {
  brand: ClientBrand;
  active_account_count: number;
  total_followers: number;
  posts_published_last_7d: number;
  posts_scheduled_next_7d: number;
  last_snapshot_date: string | null;
  last_snapshot_followers: number | null;
  follower_delta_7d: number | null;
  platforms_connected: string[];
  // Agency health fields (2026-10-01) — optional so an older API build still renders.
  posts_scheduled_next_14d?: number;
  next_scheduled_at?: string | null;
  empty_days_next_7d?: number;
  last_published_at?: string | null;
  failed_posts_14d?: number;
  scheduled_unapproved_7d?: number;
  pending_approvals?: number;
  unread_inbox?: number;
  account_issues?: ClientAccountIssue[];
  engagement_trend_14d?: number[];
  health?: ClientHealth;
  attention_count?: number;
}

export type ClientHealth = "healthy" | "attention" | "critical";

/**
 * A brand's default reel look (migration 071, lane H6 — 2026-10-05 reel
 * editing round 2) — mirrors `schemas/brand_video_style.py:BrandVideoStyle`.
 * Always the EFFECTIVE style (every key filled from the system default
 * where the brand has no override) — never the sparse stored row.
 */
export interface BrandVideoStyle {
  /** Engine caption style id (e.g. "hormozi"), or null = engine/system default. */
  caption_preset: string | null;
  /** 6-digit hex (e.g. "#F5A623"), or null = no brand override. */
  highlight_color: string | null;
  /** Display font family name, or null = no brand override. */
  font_family: string | null;
  /** Append a branded end card to Video Autopilot reels. */
  end_card_enabled: boolean;
  end_card_cta_text: string;
  /** Reserved for a future lower-third overlay default. */
  lower_third_enabled: boolean;
  /** Video Autopilot silent_text_reel music bed: "procedural", or null = no bed. */
  music_bed: string | null;
  /** Procedural music-bed mood (only meaningful when music_bed === "procedural"). */
  music_bed_mood: string | null;
  /** Schedule beat-synced visual accents on a silent_text_reel. */
  beat_accents: boolean;
}

/**
 * Partial update for PUT /api/client-brands/{id}/video-style. Same
 * `undefined`-vs-`""` convention as `ClientBrandInput`: a key left out of the
 * object leaves the stored value unchanged; an explicit `""` on
 * `caption_preset` / `highlight_color` / `font_family` / `music_bed` /
 * `music_bed_mood` clears that override back to the system default.
 */
export interface BrandVideoStyleInput {
  caption_preset?: string;
  highlight_color?: string;
  font_family?: string;
  end_card_enabled?: boolean;
  end_card_cta_text?: string;
  lower_third_enabled?: boolean;
  music_bed?: string;
  music_bed_mood?: string;
  beat_accents?: boolean;
}

export interface ClientAccountIssue {
  account_id: string;
  client_brand_id: string | null;
  platform: string;
  account_name: string;
  issue: "expired" | "expiring" | "disconnected" | "error";
  token_expires_at: string | null;
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

// Client brands + their KPI rollup change on the order of minutes (new brand
// onboarded, snapshot recomputed), not seconds. A 60s staleTime avoids a
// refetch every time the Clients tab is re-selected while keeping the data
// fresh enough for an operator dashboard.
const CLIENT_BRAND_STALE_TIME = 60_000;

/**
 * The ACTIVE SOCIAL CLIENTS (plus parents of active sub-brands) — what every
 * client picker lists. Web-only clients (`is_social_active: false`) and
 * archived ones are excluded server-side; see `useAllClientBrands` for those.
 */
export function useClientBrands() {
  return useApiQuery<{ items: ClientBrand[] }>(socialKeys.list("client_brands"), "/api/client-brands", {
    staleTime: CLIENT_BRAND_STALE_TIME,
    schema: objectResponseGuard<{ items: ClientBrand[] }>(),
  });
}

export function useClientBrandOverviews() {
  return useApiQuery<{ items: ClientBrandOverview[] }>(
    socialKeys.list("client_brand_overviews"),
    "/api/client-brands/overview/all",
    { staleTime: CLIENT_BRAND_STALE_TIME, schema: objectResponseGuard<{ items: ClientBrandOverview[] }>() }
  );
}

/** Every client incl. archived + not-on-social (the Clients page "Archived" / "Other clients" lists). */
export function useAllClientBrands() {
  return useApiQuery<{ items: ClientBrand[] }>(
    socialKeys.list("client_brands", { include_archived: true }),
    "/api/client-brands",
    {
      staleTime: CLIENT_BRAND_STALE_TIME,
      axiosConfig: { params: { include_archived: true } },
      schema: objectResponseGuard<{ items: ClientBrand[] }>(),
    }
  );
}

/** Everything that shows a client name, KPI card or client-scoped attention row. */
function invalidateClientViews(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: socialKeys.resource("client_brands") });
  qc.invalidateQueries({ queryKey: socialKeys.resource("client_brand_overviews") });
  qc.invalidateQueries({ queryKey: socialKeys.resource("attention") });
}

/** Pull the API's plain-English `detail` out of an axios error (422s carry a list). */
export function clientBrandErrorMessage(err: unknown): string | null {
  type Body = { detail?: unknown; error?: unknown } | null | undefined;
  const e = err as { responseData?: Body; response?: { data?: Body } } | null;
  const data = e?.responseData ?? e?.response?.data;
  const detail = data?.detail ?? data?.error;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: unknown; loc?: unknown };
    const field = Array.isArray(first.loc) ? String(first.loc[first.loc.length - 1]) : "";
    const msg = typeof first.msg === "string" ? first.msg.replace(/^Value error, /, "") : "";
    if (msg) return field && field !== "body" ? `${field.replace(/_/g, " ")}: ${msg}` : msg;
  }
  return null;
}

/** POST /api/client-brands — add a client (slug derived server-side from the name). */
export function useCreateClientBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ClientBrandInput & { name: string }) => {
      const res = await client.post<{ success: boolean; data: ClientBrand }>("/api/client-brands", input);
      return res.data.data;
    },
    onSuccess: () => invalidateClientViews(qc),
  });
}

/** PUT /api/client-brands/{id} — edit / archive / restore a client. */
export function useUpdateClientBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: ClientBrandInput }) => {
      const res = await client.put<{ success: boolean; data: ClientBrand }>(`/api/client-brands/${id}`, input);
      return res.data.data;
    },
    onSuccess: () => invalidateClientViews(qc),
  });
}

// ── Video style (default reel look, migration 071) ──────────────────────────────

/** GET /api/client-brands/{id}/video-style — the brand's effective reel look. */
export function useBrandVideoStyle(clientBrandId: string | null | undefined) {
  return useApiQuery<BrandVideoStyle>(
    socialKeys.detail("brand_video_style", clientBrandId ?? "none"),
    `/api/client-brands/${clientBrandId}/video-style`,
    {
      enabled: Boolean(clientBrandId),
      staleTime: CLIENT_BRAND_STALE_TIME,
      schema: objectResponseGuard<BrandVideoStyle>(),
    }
  );
}

/** PUT /api/client-brands/{id}/video-style — set this brand's default reel look. */
export function useUpdateBrandVideoStyle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientBrandId, input }: { clientBrandId: string; input: BrandVideoStyleInput }) => {
      const res = await client.put<{ success: boolean; data: BrandVideoStyle }>(
        `/api/client-brands/${clientBrandId}/video-style`,
        input
      );
      return res.data.data;
    },
    onSuccess: (_data, { clientBrandId }) => {
      qc.invalidateQueries({ queryKey: socialKeys.detail("brand_video_style", clientBrandId) });
    },
  });
}
